import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, BookText, PencilLine, FileBarChart,
  FolderArchive, Store, ShieldCheck, Settings as SettingsIcon, LogOut, Building2,
  PanelLeftClose, PanelLeftOpen, ScanLine, PiggyBank, ChevronLeft,
  ReceiptText, Users, TrendingUp, BarChart3, PackageCheck, Car, Zap, Percent, Landmark, BookOpenCheck, Boxes,
} from "lucide-react";

const NAV_GROUPS = [
  { items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  { title: "Dagboek", items: [
    { to: "/journal", label: "Dagontvangsten", icon: BookText },
    { to: "/corrections", label: "Correcties", icon: PencilLine },
    { to: "/z-inbox", label: "Z-tickets (gsm)", icon: ScanLine },
  ] },
  { title: "Boeken", items: [
    { to: "/kasboek", label: "Kasboek", icon: PiggyBank, end: true },
    { to: "/factuurboek", label: "Factuurboek", icon: ReceiptText, end: true },
    { to: "/factuurboek/leveranciers", label: "Leveranciers", icon: Users },
    { to: "/factuurboek/leveranciersrekening", label: "Leveranciersrekening", icon: Landmark },
    { to: "/providers", label: "Bank & Betaalproviders", icon: Landmark },
    { to: "/grootboek", label: "Grootboek", icon: BookOpenCheck },
    { to: "/vaste-activa", label: "Vaste activa", icon: Boxes },
  ] },
  { title: "Rapporten", items: [
    { to: "/reports", label: "BTW-rapporten", icon: FileBarChart },
    { to: "/rapporten/opbrengsten", label: "Opbrengsten dagboek", icon: TrendingUp },
    { to: "/rapporten/overzicht", label: "Aankopen-overzicht", icon: BarChart3 },
    { to: "/rapporten/diverse", label: "Diverse opbrengsten", icon: PackageCheck },
    { to: "/rapporten/autokosten", label: "Autokosten", icon: Car },
    { to: "/rapporten/energie", label: "Energie", icon: Zap },
    { to: "/rapporten/marge", label: "Marge per type", icon: Percent },
  ] },
  { title: "Beheer", items: [
    { to: "/documents", label: "Bewijsstukken", icon: FolderArchive },
    { to: "/locations", label: "Vestigingen", icon: Store },
    { to: "/audit", label: "Audit trail", icon: ShieldCheck },
    { to: "/settings", label: "Instellingen", icon: SettingsIcon },
  ] },
];

const ROLE_LABEL = { admin: "Beheerder", boekhouder: "Boekhouder", inspecteur: "Inspecteur" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");

  const toggle = () => {
    setCollapsed((c) => {
      localStorage.setItem("sidebar_collapsed", c ? "0" : "1");
      return !c;
    });
  };

  const navClass = (isActive) =>
    `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 ${collapsed ? "justify-center" : ""} ${
      isActive
        ? "bg-primary text-primary-foreground"
        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
    }`;

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        className={`no-print shrink-0 border-r border-border bg-card flex flex-col fixed h-screen transition-[width] duration-300 ${collapsed ? "w-[68px]" : "w-64"}`}
      >
        <div className="px-3 py-5 border-b border-border flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground shrink-0">
            <Building2 className="h-5 w-5" />
          </div>
          {!collapsed && (
            <div className="leading-tight overflow-hidden">
              <div className="font-heading font-semibold text-sm truncate">Singh Express Shop</div>
              <div className="text-xs text-muted-foreground truncate">Boekhouding</div>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            data-testid="sidebar-toggle"
            className={`h-8 w-8 text-muted-foreground hover:text-foreground ${collapsed ? "mx-auto" : "ml-auto"}`}
            onClick={toggle}
            title={collapsed ? "Zijbalk openen" : "Zijbalk sluiten"}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          <button
            onClick={() => navigate("/hub")}
            title={collapsed ? "Modules" : undefined}
            data-testid="nav-back-modules"
            className={`w-full flex items-center gap-3 rounded-md px-3 py-2 mb-1 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 ${collapsed ? "justify-center" : ""}`}
          >
            <ChevronLeft className="h-4 w-4 shrink-0" />
            {!collapsed && "Modules"}
          </button>
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi} className="space-y-0.5">
              {group.title && !collapsed && (
                <div className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {group.title}
                </div>
              )}
              {group.title && collapsed && gi > 0 && (
                <div className="my-2 border-t border-border/60" />
              )}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  title={collapsed ? item.label : undefined}
                  data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1).replace(/\//g, "-")}`}
                  className={({ isActive }) => navClass(isActive)}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          {!collapsed && (
            <div className="px-2 py-1.5 mb-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground">{ROLE_LABEL[user?.role] || user?.role}</div>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            data-testid="logout-btn"
            className={`w-full text-muted-foreground hover:text-foreground ${collapsed ? "justify-center px-0" : "justify-start"}`}
            onClick={() => { logout(); navigate("/"); }}
            title={collapsed ? "Afmelden" : undefined}
          >
            <LogOut className="h-4 w-4" />
            {!collapsed && <span className="ml-2">Afmelden</span>}
          </Button>
        </div>
      </aside>

      <main className={`flex-1 min-h-screen transition-[margin] duration-300 ${collapsed ? "ml-[68px]" : "ml-64"}`}>
        <div className="px-6 lg:px-10 py-8 max-w-[1400px]">{children}</div>
      </main>
    </div>
  );
}
